import { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'motion/react';
import { useScene } from '../scene-context';
import { SceneMedia } from '../SceneMedia';
import { Particles } from '../Particles';
import { TiltCard } from '../interactive/TiltCard';
import { DecorLayer } from '../decor/DecorLayer';
import { PixarBlurText, PixarFloatingBadge } from '../interactive/PixarBlurText';
import { RoyalGoldSmokeText, RoyalGoldCrestBadge } from '../interactive/RoyalGoldSmokeText';
import type { SceneHero } from '../manifest';

interface HeroSectionProps {
  hero: SceneHero;
}

function getHeroCardStyle(packId: string, colors: { primary: string }) {
  if (packId === 'heritage') {
    return {
      backgroundColor: 'rgba(28,6,8,0.72)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: `1px solid ${colors.primary}55`,
      boxShadow: `0 24px 80px rgba(0,0,0,0.7), inset 0 0 24px ${colors.primary}20`,
    };
  }
  if (packId === 'pastel_dream') {
    return {
      backgroundColor: 'rgba(31, 11, 24, 0.82)',
      backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)',
      border: '2px solid rgba(249, 168, 212, 0.55)',
      boxShadow: '0 24px 80px rgba(0,0,0,0.75), inset 0 0 24px rgba(249, 168, 212, 0.2)',
    };
  }
  if (packId === 'modern_noir') {
    return {
      backgroundColor: 'rgba(5, 5, 10, 0.88)',
      backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)',
      border: '2px solid rgba(250, 204, 21, 0.6)',
      boxShadow: '0 24px 80px rgba(0,0,0,0.9), inset 0 0 24px rgba(250, 204, 21, 0.2)',
    };
  }
  if (packId === 'midnight_glow') {
    return {
      backgroundColor: 'rgba(2, 6, 23, 0.88)',
      backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)',
      border: '2px solid rgba(14, 165, 233, 0.6)',
      boxShadow: '0 24px 80px rgba(0,0,0,0.9)',
    };
  }
  return {
    backgroundColor: 'rgba(10, 10, 15, 0.75)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: `1px solid ${colors.primary}55`,
    boxShadow: '0 24px 80px rgba(0,0,0,0.65)',
  };
}

export function HeroSection({ hero }: HeroSectionProps) {
  const { pack, manifest } = useScene();
  const prefersReducedMotion = useReducedMotion();
  const heroArtRef = useRef<HTMLElement>(null);

  const { scrollYProgress: heroArtScroll } = useScroll({
    target: heroArtRef,
    offset: ['start start', 'end start'],
  });
  const heroZoomScale = useTransform(heroArtScroll, [0, 1], [1.0, 1.22]);
  const heroContentY = useTransform(heroArtScroll, [0, 1], [0, -70]);
  const heroContentOpacity = useTransform(heroArtScroll, [0, 0.65], [1, 0]);

  const primary = hero.primaryName ?? hero.brideName ?? '';
  const secondary = hero.secondaryName ?? hero.groomName ?? '';
  const date = hero.eventDate ?? hero.weddingDate;
  const nameTreatment = manifest.typography?.nameTreatment ?? 'calligraphy_reveal';
  const nameFont =
    pack.id === 'indo_ghibli'
      ? pack.fonts.display
      : pack.id === 'storybook_3d'
      ? pack.fonts.display
      : pack.id === 'pichwai_heritage' || pack.id === 'heritage'
      ? pack.fonts.display
      : nameTreatment === 'editorial_serif'
      ? pack.fonts.display
      : pack.fonts.script ?? pack.fonts.display;

  const isFestival = nameTreatment === 'festival_script';
  const nameGradient = pack.id === 'indo_ghibli'
    ? 'linear-gradient(135deg, #FFFFFF 0%, #FDE68A 60%, #F59E0B 100%)'
    : pack.id === 'storybook_3d'
    ? 'linear-gradient(135deg, #FFFFFF 0%, #FED7AA 35%, #F97316 70%, #EA580C 100%)'
    : pack.id === 'pichwai_heritage' || pack.id === 'heritage'
    ? 'linear-gradient(135deg, #FFFBEB 0%, #FEF08A 25%, #D4AF37 60%, #92400E 100%)'
    : isFestival
    ? `linear-gradient(100deg, #FFFFFF, #FFE6B0, ${pack.colors.primary})`
    : `linear-gradient(100deg, #FFFFFF, ${pack.colors.primary}, #FFFFFF)`;

  const isSinglePersonOccasion =
    pack.id === 'midnight_glow' ||
    pack.id === 'modern_noir' ||
    pack.id === 'pastel_dream' ||
    manifest.occasion === 'birthday' ||
    manifest.occasion === 'baby_shower' ||
    manifest.occasion === 'send_off';

  const mobileBehavior = {
    disableVideo: hero.background.renderStrategy === 'image_primary' || hero.background.particleEffect !== 'none',
    reduceParticles: true,
  };

  const backgroundLayer = (
    <>
      <div className="absolute inset-0 w-full h-full z-0">
        <SceneMedia assets={hero.background} mobileBehavior={mobileBehavior} />
      </div>
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{ background: pack.vignette }}
        aria-hidden
      />
      
      {/* Volumetric Radial Aura Pulse (Glowing lights/candles effect) */}
      <div
        className="absolute inset-0 pointer-events-none animate-pulse z-0"
        style={{
          animationDuration: pack.id === 'modern_noir' ? '3s' : '2s',
          background:
            pack.id === 'midnight_glow'
              ? 'radial-gradient(ellipse at 50% 30%, rgba(14,165,233,0.35) 0%, rgba(255,215,0,0.15) 50%, transparent 80%)'
              : pack.id === 'modern_noir'
              ? 'radial-gradient(ellipse at 50% 20%, rgba(0,212,255,0.25) 0%, rgba(0,0,0,0.6) 60%, transparent 100%)'
              : pack.id === 'pastel_dream'
              ? 'radial-gradient(ellipse at 50% 80%, rgba(255,180,80,0.35) 0%, rgba(255,100,0,0.15) 30%, transparent 70%), radial-gradient(ellipse at 30% 60%, rgba(255,200,100,0.2) 0%, transparent 50%)'
              : undefined,
        }}
        aria-hidden
      />

      <Particles effect={hero.background.particleEffect} />
      <DecorLayer decor={hero.decor} variant="hero" />
    </>
  );

  return (
    <>
      {/* Frame 1: Pure 100% Unblocked Artwork (with name added per user request) */}
      <section
        ref={heroArtRef}
        className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
        data-section-id="hero-art"
      >
        <motion.div
          style={{ scale: prefersReducedMotion ? 1 : heroZoomScale }}
          className="absolute inset-0 w-full h-full z-0"
        >
          {backgroundLayer}
        </motion.div>
        
        {/* Render Name & Birthday Person Photo Directly on First Frame */}
        <motion.div
          style={{
            y: prefersReducedMotion ? 0 : heroContentY,
            opacity: prefersReducedMotion ? 1 : heroContentOpacity,
          }}
          className="relative z-10 text-center px-6 max-w-4xl w-full pt-10 pb-20 pointer-events-none drop-shadow-2xl"
        >
          {/* Birthday Person Photo Frame for Birthday Packs */}
          {(pack.id === 'midnight_glow' || pack.id === 'modern_noir' || pack.id === 'pastel_dream') && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-6 relative inline-block group pointer-events-auto"
            >
              {/* Outer Glow Ring */}
              <div
                className={`absolute -inset-2 rounded-full blur-md opacity-75 animate-pulse ${
                  pack.id === 'midnight_glow'
                    ? 'bg-gradient-to-r from-sky-500 via-amber-400 to-cyan-500'
                    : pack.id === 'modern_noir'
                    ? 'bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600'
                    : 'bg-gradient-to-r from-pink-400 via-rose-300 to-amber-300'
                }`}
              />

              {/* Portrait Container */}
              <div
                className={`relative w-36 h-36 md:w-44 md:h-44 rounded-full overflow-hidden border-4 shadow-2xl bg-slate-900/90 flex items-center justify-center ${
                  pack.id === 'midnight_glow'
                    ? 'border-sky-400 shadow-sky-500/50'
                    : pack.id === 'modern_noir'
                    ? 'border-yellow-400 shadow-yellow-500/50'
                    : 'border-pink-300 shadow-pink-400/50'
                }`}
              >
                <img
                  src={
                    (hero as any).personPhotoUrl ||
                    (hero as any).personPhoto?.publicUrl ||
                    (hero as any).referenceImages?.primary ||
                    (pack.id === 'midnight_glow'
                      ? '/assets/birthday/ironman_birthday_celebration_real.jpg'
                      : pack.id === 'modern_noir'
                      ? '/assets/birthday/batman_joker_birthday.jpg'
                      : '/assets/birthday/barbie_princess_castle.jpg')
                  }
                  alt={primary || 'Birthday Person'}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/assets/birthday/ironman_birthday_celebration_real.jpg';
                  }}
                />
              </div>

              {/* Themed Badge Overlay */}
              <div
                className={`absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider text-white shadow-lg backdrop-blur-md border ${
                  pack.id === 'midnight_glow'
                    ? 'bg-slate-950/90 border-sky-400 text-sky-300 shadow-sky-500/40'
                    : pack.id === 'modern_noir'
                    ? 'bg-zinc-950/90 border-yellow-400 text-yellow-300 shadow-yellow-500/40'
                    : 'bg-pink-950/90 border-pink-300 text-pink-200 shadow-pink-400/40'
                }`}
              >
                {pack.id === 'midnight_glow'
                  ? '⚡ STAR OF THE DAY'
                  : pack.id === 'modern_noir'
                  ? 'BATMAN GOTHAM HERO'
                  : '👑 BIRTHDAY PRINCESS'}
              </div>
            </motion.div>
          )}

          {/* Top Occasion Pill / Badge */}
          {pack.id === 'indo_ghibli' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-4 inline-flex items-center gap-2 px-5 py-1.5 rounded-full border border-amber-400/40 bg-black/40 backdrop-blur-md shadow-lg"
            >
              <span className="text-amber-300 text-xs tracking-[0.25em] font-serif uppercase">
                ✦ A STUDIO GHIBLI FAIRYTALE ✦
              </span>
            </motion.div>
          )}

          {pack.id === 'storybook_3d' && (
            <div className="mb-4">
              <PixarFloatingBadge delay={0.1} icon="✨">
                A DISNEY PIXAR 3D FAIRYTALE
              </PixarFloatingBadge>
            </div>
          )}

          {(pack.id === 'pichwai_heritage' || pack.id === 'heritage') && (
            <div className="mb-4">
              <RoyalGoldCrestBadge delay={0.1} icon="⚜️">
                MEWAR PICHWAI SHUBH VIVAH
              </RoyalGoldCrestBadge>
            </div>
          )}

          {hero.tagline && (
            <motion.p
              initial={{ filter: 'blur(12px)', opacity: 0, y: 10 }}
              animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="text-xs md:text-sm uppercase tracking-widest mb-4 opacity-95 font-bold"
              style={{ color: pack.colors.primary, fontFamily: pack.fonts.body, textShadow: `0 2px 10px rgba(0,0,0,0.8)` }}
            >
              {hero.tagline}
            </motion.p>
          )}

          <motion.h1
            initial={{ filter: 'blur(28px) brightness(2.2)', opacity: 0, scale: 0.94 }}
            animate={{ filter: 'blur(0px) brightness(1.0)', opacity: 1, scale: 1.0 }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="leading-tight"
            style={{
              color: '#FFFFFF',
              fontFamily: nameFont,
              fontSize: 'clamp(3rem, 9vw, 7rem)',
              fontWeight: 700,
              textShadow: `0 4px 28px rgba(0,0,0,0.8), 0 0 34px ${pack.colors.primary}66`,
            }}
          >
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: nameGradient }}>
              {primary}
            </span>
            {secondary && !isSinglePersonOccasion && (
              <>
                <span
                  className="inline-block px-3 font-normal"
                  style={{ fontFamily: pack.fonts.display, color: pack.colors.primary }}
                >
                  &amp;
                </span>
                <span className="bg-clip-text text-transparent" style={{ backgroundImage: nameGradient }}>
                  {secondary}
                </span>
              </>
            )}
          </motion.h1>
          {secondary && isSinglePersonOccasion && (
            <p
              className="mt-2 text-xl md:text-3xl font-bold tracking-wide uppercase"
              style={{ color: pack.colors.primary, fontFamily: pack.fonts.body, textShadow: `0 2px 14px rgba(0,0,0,0.9)` }}
            >
              {secondary}
            </p>
          )}

          {/* Date & Location directly on Hero Frame 1 */}
          {(pack.id === 'indo_ghibli' || pack.id === 'storybook_3d' || pack.id === 'pichwai_heritage' || pack.id === 'heritage') && date && (
            <motion.div
              initial={{ filter: 'blur(16px)', opacity: 0, y: 15 }}
              animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
              transition={{ duration: 0.85, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 flex flex-col items-center gap-1.5"
            >
              <p
                className="text-base md:text-2xl font-bold tracking-[0.18em] uppercase text-amber-200"
                style={{ fontFamily: pack.fonts.display, textShadow: '0 2px 14px rgba(0,0,0,0.95)' }}
              >
                {date}
              </p>
              <p
                className="text-xs md:text-sm tracking-[0.25em] uppercase text-white/80"
                style={{ fontFamily: pack.fonts.body, textShadow: '0 1px 8px rgba(0,0,0,0.9)' }}
              >
                {pack.id === 'storybook_3d' ? 'Grand Fairytale Palace, Jaipur' : 'City Palace, Udaipur, Rajasthan'}
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* Scroll indicator chevron (fades out as user scrolls) */}
        <motion.div
          style={{ opacity: prefersReducedMotion ? 1 : heroContentOpacity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center pointer-events-none"
        >
          <p className="text-white/80 text-xs uppercase tracking-widest mb-2" style={{ fontFamily: pack.fonts.body }}>Scroll to Explore</p>
          <svg
            className="w-6 h-6 animate-bounce"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{ color: pack.colors.primary }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 13l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </motion.div>
      </section>

      {/* Frame 2: Artwork + Hero Invitation Card (Skipped for birthday, Ghibli, Storybook, and Pichwai so text isn't duplicated) */}
      {pack.id !== 'midnight_glow' &&
        pack.id !== 'modern_noir' &&
        pack.id !== 'pastel_dream' &&
        pack.id !== 'indo_ghibli' &&
        pack.id !== 'storybook_3d' &&
        pack.id !== 'pichwai_heritage' &&
        pack.id !== 'heritage' && (
        <HeroTextFrame
          pack={pack}
          prefersReducedMotion={prefersReducedMotion}
          backgroundLayer={backgroundLayer}
          hero={hero}
          nameFont={nameFont}
          nameGradient={nameGradient}
          primary={primary}
          secondary={secondary}
          isSinglePersonOccasion={isSinglePersonOccasion}
          date={date}
        />
      )}
    </>
  );
}

function HeroTextFrame({
  pack,
  prefersReducedMotion,
  backgroundLayer,
  hero,
  nameFont,
  nameGradient,
  primary,
  secondary,
  isSinglePersonOccasion,
  date,
}: {
  pack: any;
  prefersReducedMotion: boolean | null;
  backgroundLayer: React.ReactNode;
  hero: any;
  nameFont: string;
  nameGradient: string;
  primary: string;
  secondary: string;
  isSinglePersonOccasion: boolean;
  date: string | null;
}) {
  const heroTextRef = useRef<HTMLElement>(null);
  const { scrollYProgress: heroTextScroll } = useScroll({
    target: heroTextRef,
    offset: ['start end', 'end start'],
  });
  const heroTextZoomScale = useTransform(heroTextScroll, [0, 1], [1.15, 1.02]);

  return (
    <section
      ref={heroTextRef}
      className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
      data-section-id="hero-text"
    >
      <motion.div
        style={{ scale: prefersReducedMotion ? 1 : heroTextZoomScale }}
        className="absolute inset-0 w-full h-full z-0"
      >
        {backgroundLayer}
      </motion.div>

      <div className="relative z-10 text-center px-6 max-w-4xl w-full pt-20 pb-16">
        <TiltCard intensity={15}>
          <div
            className="px-8 py-10 rounded-3xl"
            style={getHeroCardStyle(pack.id, pack.colors)}
          >
            {hero.tagline && (
              <p
                className="text-xs md:text-sm uppercase tracking-widest mb-6 opacity-95 font-bold"
                style={{
                  color: pack.colors.primary,
                  fontFamily: pack.fonts.body,
                  textShadow: `0 2px 16px ${pack.colors.background}`,
                }}
              >
                {hero.tagline}
              </p>
            )}
            <h1
              className="mb-6 leading-tight"
              style={{
                color: '#FFFFFF',
                fontFamily: nameFont,
                fontSize: 'clamp(3rem, 9vw, 7rem)',
                fontWeight: 600,
                textShadow: `0 4px 28px rgba(0,0,0,0.8), 0 0 34px ${pack.colors.primary}66`,
              }}
            >
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: nameGradient }}>
                {primary}
              </span>
              {secondary && !isSinglePersonOccasion && (
                <>
                  <span
                    className="inline-block px-3 font-normal"
                    style={{
                      fontFamily: pack.fonts.display,
                      color: pack.colors.primary,
                    }}
                  >
                    &amp;
                  </span>
                  <span className="bg-clip-text text-transparent" style={{ backgroundImage: nameGradient }}>
                    {secondary}
                  </span>
                </>
              )}
            </h1>
            {secondary && isSinglePersonOccasion && (
              <p
                className="mb-6 text-xl md:text-3xl font-bold tracking-wide uppercase"
                style={{ color: pack.colors.primary, fontFamily: pack.fonts.body }}
              >
                {secondary}
              </p>
            )}
            {date && (
              <p
                className="text-lg md:text-2xl font-bold opacity-95"
                style={{ color: '#FFFFFF', fontFamily: pack.fonts.body, letterSpacing: 0 }}
              >
                {date}
              </p>
            )}
          </div>
        </TiltCard>
      </div>
    </section>
  );
}
